import {
  canAccessAgent,
  AGENT_DASHBOARD_AGENTS,
} from "./agentDashboardConfig";
import {
  canAccessDevopsWorkspace,
  canAccessKafkaEventHub,
  canManageApiHub,
  canManageGatewayRoutes,
  isProductManager,
  isProductionSupportAdmin,
  isSuperAdmin,
  isTenantAdmin,
} from "./appRoles";

/** Access levels enforced at the route layer (mirrors nav ACL flags). */
export type RouteAccessKind =
  | "public"
  | "auth"
  | "productManager"
  | "tenantAdmin"
  | "superAdmin"
  | "architect"
  | "productionSupport"
  | "integrationSupport"
  | "gatewayRouteHub"
  | "kafkaEventHub"
  | "apiHub"
  | "documentHub"
  | "operationalErrorHub"
  | "devops";

export interface RouteAccessRule {
  prefix: string;
  access: RouteAccessKind;
  /** When true, only the exact path matches (not sub-paths). */
  exact?: boolean;
}

/**
 * Ordered most-specific first. First match wins.
 * Add new protected routes here so menu and router stay aligned.
 */
export const ROUTE_ACCESS_RULES: RouteAccessRule[] = [
  { prefix: "/agent-dashboard/catalog-assistant", access: "productManager" },
  { prefix: "/agent-dashboard/architect", access: "architect" },
  { prefix: "/agent-dashboard/production-support", access: "productionSupport" },
  { prefix: "/agent-dashboard/integration-support", access: "integrationSupport" },
  { prefix: "/agent-dashboard/devops", access: "devops" },
  { prefix: "/devops", access: "devops" },
  { prefix: "/product-manager-dashboard/gateway-route-hub", access: "gatewayRouteHub" },
  { prefix: "/product-manager-dashboard/kafka-event-hub", access: "kafkaEventHub" },
  { prefix: "/product-manager-dashboard/api-hub", access: "apiHub" },
  { prefix: "/product-manager-dashboard/architecture-standards-hub", access: "architect" },
  { prefix: "/product-manager-dashboard/document-hub", access: "documentHub" },
  { prefix: "/product-manager-dashboard/knowledge-search", access: "documentHub" },
  { prefix: "/product-manager-dashboard/operational-error-hub", access: "operationalErrorHub" },
  { prefix: "/product-manager-dashboard", access: "productManager" },
  { prefix: "/manage-assets", access: "productManager" },
  { prefix: "/analytics-dashboard", access: "productManager", exact: true },
  { prefix: "/create-product", access: "productManager", exact: true },
  { prefix: "/subscription-requests", access: "productManager" },
  { prefix: "/connectors-dashboard", access: "tenantAdmin" },
  { prefix: "/platform-dashboard", access: "superAdmin" },
  { prefix: "/settings", access: "tenantAdmin", exact: true },
  { prefix: "/org-admin", access: "tenantAdmin", exact: true },
  { prefix: "/api-explorer", access: "auth" },
  { prefix: "/kafka-event-explorer", access: "auth" },
  { prefix: "/product-explorer", access: "auth", exact: true },
  { prefix: "/category-explorer", access: "auth" },
  { prefix: "/my-subscriptions", access: "auth", exact: true },
  { prefix: "/add-subscription", access: "auth", exact: true },
  { prefix: "/support-cases", access: "auth" },
];

export type NavAccessFlags = {
  /** Takes precedence over the boolean flags when set. */
  access?: RouteAccessKind;
  requiresAuth?: boolean;
  requiresProductManager?: boolean;
  requiresSuperAdmin?: boolean;
  requiresTenantAdmin?: boolean;
};

export interface RouteAccessContext {
  roles?: string[] | null;
  hasToken: boolean;
}

export interface AccessDeniedContent {
  pageTitle: string;
  pageDescription: string;
  message: string;
}

const ARCHITECT_AGENT = AGENT_DASHBOARD_AGENTS.find((a) => a.id === "architect");
const PRODUCTION_SUPPORT_AGENT = AGENT_DASHBOARD_AGENTS.find(
  (a) => a.id === "production-support",
);
const INTEGRATION_SUPPORT_AGENT = AGENT_DASHBOARD_AGENTS.find(
  (a) => a.id === "integration-analyst",
);

export function normalizePathname(pathname: string): string {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

export function pathMatchesRule(pathname: string, rule: RouteAccessRule): boolean {
  const normalized = normalizePathname(pathname);
  if (rule.exact) {
    return normalized === rule.prefix;
  }
  return (
    normalized === rule.prefix || normalized.startsWith(`${rule.prefix}/`)
  );
}

export function getRouteAccessKind(pathname: string): RouteAccessKind {
  const rule = ROUTE_ACCESS_RULES.find((r) => pathMatchesRule(pathname, r));
  return rule?.access ?? "public";
}

export function navFlagsToAccessKind(flags: NavAccessFlags): RouteAccessKind {
  if (flags.access) return flags.access;
  if (flags.requiresProductManager) return "productManager";
  if (flags.requiresSuperAdmin) return "superAdmin";
  if (flags.requiresTenantAdmin) return "tenantAdmin";
  if (flags.requiresAuth) return "auth";
  return "public";
}

export function checkRouteAccess(
  access: RouteAccessKind,
  ctx: RouteAccessContext,
): boolean {
  const roles = ctx.roles;

  switch (access) {
    case "public":
      return true;
    case "auth":
      return ctx.hasToken;
    case "productManager":
      return isProductManager(roles);
    case "tenantAdmin":
      return isTenantAdmin(roles);
    case "superAdmin":
      return isSuperAdmin(roles);
    case "architect":
      return ARCHITECT_AGENT
        ? canAccessAgent(roles ?? [], ARCHITECT_AGENT.access)
        : false;
    case "productionSupport":
      return (
        (PRODUCTION_SUPPORT_AGENT
          ? canAccessAgent(roles ?? [], PRODUCTION_SUPPORT_AGENT.access)
          : false) || isProductManager(roles)
      );
    case "integrationSupport":
      return INTEGRATION_SUPPORT_AGENT
        ? canAccessAgent(roles ?? [], INTEGRATION_SUPPORT_AGENT.access)
        : false;
    case "gatewayRouteHub":
      return canManageGatewayRoutes(roles);
    case "kafkaEventHub":
      return canAccessKafkaEventHub(roles);
    case "apiHub":
      return canManageApiHub(roles);
    case "documentHub":
    case "operationalErrorHub":
      return isProductManager(roles) || isProductionSupportAdmin(roles);
    case "devops":
      return canAccessDevopsWorkspace(roles);
    default:
      return false;
  }
}

export function getAccessDeniedContent(
  access: RouteAccessKind,
): AccessDeniedContent {
  switch (access) {
    case "auth":
      return {
        pageTitle: "Sign in required",
        pageDescription: "You must be signed in to view this page.",
        message: "Sign in through your organization portal to continue.",
      };
    case "productManager":
      return {
        pageTitle: "Access denied",
        pageDescription:
          "You do not have permission to view the Product Manager area.",
        message:
          "This area requires the product-manager role. Contact your administrator if you need access.",
      };
    case "tenantAdmin":
      return {
        pageTitle: "Access denied",
        pageDescription: "You do not have permission to view this admin area.",
        message:
          "This area requires the admin or tenant-admin role. Contact your administrator if you need access.",
      };
    case "superAdmin":
      return {
        pageTitle: "Access denied",
        pageDescription:
          "You do not have permission to view the Platform Dashboard.",
        message:
          "This area requires the SAMI X Admin role. Contact your administrator if you need access.",
      };
    case "architect":
      return {
        pageTitle: "Access denied",
        pageDescription: "The Architect Agent requires the Architect role.",
        message:
          "This area requires the Architect role. Contact your administrator if you need access.",
      };
    case "productionSupport":
      return {
        pageTitle: "Access denied",
        pageDescription:
          "The Production Support Operator Hub requires CSR / prod-support access.",
        message:
          "This area requires the CSR or prod-support role (or product manager). Contact your administrator if you need access.",
      };
    case "integrationSupport":
      return {
        pageTitle: "Access denied",
        pageDescription:
          "The Integration Support Analyst requires a registered partner account.",
        message:
          "This area is for registered users (user / merchant role). Sign in or contact your administrator if you need access.",
      };
    case "gatewayRouteHub":
      return {
        pageTitle: "Access denied",
        pageDescription: "Gateway Route Hub requires an internal role.",
        message:
          "This area requires the Architect, product-manager, or gateway-developer role. Contact your administrator if you need access.",
      };
    case "kafkaEventHub":
      return {
        pageTitle: "Access denied",
        pageDescription:
          "The Kafka Event Registry requires the Architect or product-manager role.",
        message:
          "This area requires the Architect or product-manager role. Contact your administrator if you need access.",
      };
    case "apiHub":
      return {
        pageTitle: "Access denied",
        pageDescription: "API Hub requires the Architect or product-manager role.",
        message:
          "This area requires the Architect or product-manager role. Contact your administrator if you need access.",
      };
    case "documentHub":
      return {
        pageTitle: "Access denied",
        pageDescription: "Document Hub requires product-manager or prod-support-admin access.",
        message:
          "This area requires the product-manager or prod-support-admin role. Contact your administrator if you need access.",
      };
    case "operationalErrorHub":
      return {
        pageTitle: "Access denied",
        pageDescription:
          "Operational Error Hub requires product-manager or prod-support-admin access.",
        message:
          "This area requires the product-manager or prod-support-admin role. Contact your administrator if you need access.",
      };
    case "devops":
      return {
        pageTitle: "Access denied",
        pageDescription:
          "The DevOps workspace requires an environment-manager or devops-operator role.",
        message:
          "This area requires the environment-manager or devops-operator role. Contact your administrator if you need access.",
      };
    default:
      return {
        pageTitle: "Access denied",
        pageDescription: "You do not have permission to view this page.",
        message: "Contact your administrator if you need access.",
      };
  }
}
